function toDateKey(value) {
  if (!value) return null;
  if (typeof value === "string") {
    const iso = value.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

/** YYYY-MM calendar month for a penalty (effective date preferred). */
export function getPenaltyMonthKey(penalty) {
  const dateKey = toDateKey(
    penalty?.date || penalty?.effectiveDate || penalty?.createdAt,
  );
  return dateKey ? dateKey.slice(0, 7) : null;
}

export function getCurrentMonthKey(referenceDate = new Date()) {
  const year = referenceDate.getFullYear();
  const month = String(referenceDate.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export function getMonthKeyOffset(referenceDate = new Date(), monthOffset = 0) {
  const date = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth() + monthOffset,
    1,
  );
  return getCurrentMonthKey(date);
}

export function formatPenaltyMonthLabel(monthKey, locale = "ar-EG") {
  if (!monthKey) return "غير محدد";
  const [year, month] = monthKey.split("-").map(Number);
  if (!year || !month) return monthKey;
  return new Date(year, month - 1, 1).toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
  });
}

/** HR / employee penalty lists keep the current month plus the previous month. */
export const PENALTY_LIST_RECENT_MONTHS = 2;

export function getRecentMonthKeys(
  recentMonths = PENALTY_LIST_RECENT_MONTHS,
  referenceDate = new Date(),
) {
  const count = Math.max(1, Math.floor(Number(recentMonths)) || 1);
  return Array.from({ length: count }, (_, index) =>
    getMonthKeyOffset(referenceDate, -index),
  );
}

export function isPenaltyInRecentMonths(
  penalty,
  recentMonths = PENALTY_LIST_RECENT_MONTHS,
  referenceDate = new Date(),
) {
  const monthKey =
    getPenaltyMonthKey(penalty) || getCurrentMonthKey(referenceDate);
  return getRecentMonthKeys(recentMonths, referenceDate).includes(monthKey);
}

export function filterPenaltiesByRecentMonths(
  penalties = [],
  recentMonths = PENALTY_LIST_RECENT_MONTHS,
  referenceDate = new Date(),
) {
  return penalties.filter((penalty) =>
    isPenaltyInRecentMonths(penalty, recentMonths, referenceDate),
  );
}

/**
 * HR company-wide list: last two calendar months, plus pending_hr items
 * that still need a decision even if they are older.
 */
export function filterHrPenaltyList(
  penalties = [],
  recentMonths = PENALTY_LIST_RECENT_MONTHS,
  referenceDate = new Date(),
) {
  return penalties.filter(
    (penalty) =>
      penalty?.status === "pending_hr" ||
      isPenaltyInRecentMonths(penalty, recentMonths, referenceDate),
  );
}

function sortPenaltiesNewestFirst(items) {
  return [...items].sort((a, b) => {
    const aDate = toDateKey(a?.date || a?.effectiveDate || a?.createdAt) || "";
    const bDate = toDateKey(b?.date || b?.effectiveDate || b?.createdAt) || "";
    return bDate.localeCompare(aDate);
  });
}

/**
 * Split penalties into the current calendar month and older months.
 * Pass `recentMonths` (e.g. 2) to keep only the latest N calendar months.
 */
export function splitPenaltiesByMonth(
  penalties = [],
  referenceDate = new Date(),
  { recentMonths } = {},
) {
  const currentKey = getCurrentMonthKey(referenceDate);
  const allowedOtherKeys =
    Number.isInteger(recentMonths) && recentMonths >= 1
      ? new Set(
          Array.from({ length: Math.max(0, recentMonths - 1) }, (_, index) =>
            getMonthKeyOffset(referenceDate, -(index + 1)),
          ),
        )
      : null;
  const currentMonth = [];
  const otherByMonth = new Map();

  for (const penalty of penalties) {
    const monthKey = getPenaltyMonthKey(penalty) || currentKey;
    if (monthKey === currentKey) {
      currentMonth.push(penalty);
      continue;
    }
    if (allowedOtherKeys && !allowedOtherKeys.has(monthKey)) {
      continue;
    }
    if (!otherByMonth.has(monthKey)) {
      otherByMonth.set(monthKey, []);
    }
    otherByMonth.get(monthKey).push(penalty);
  }

  const otherMonths = [...otherByMonth.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([monthKey, items]) => ({
      monthKey,
      label: formatPenaltyMonthLabel(monthKey),
      items: sortPenaltiesNewestFirst(items),
    }));

  return {
    currentMonthKey: currentKey,
    currentMonthLabel: formatPenaltyMonthLabel(currentKey),
    currentMonth: sortPenaltiesNewestFirst(currentMonth),
    otherMonths,
  };
}
